// ============================================================
// SKELETON LOADING COMPONENTS
// File: src/components/Skeleton.js
// Reusable skeleton loaders for better loading states
// ============================================================

import React from 'react'
import { useTheme } from '../ThemeContext'

// Base skeleton with pulsing animation
export const Skeleton = ({ width = '100%', height = '20px', className = '', style = {} }) => {
  const { colors } = useTheme()

  return (
    <div
      className={className}
      style={{
        width,
        height,
        backgroundColor: colors.border.primary,
        borderRadius: '4px',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
        ...style
      }}
    />
  )
}

// Skeleton for stat cards
export const StatCardSkeleton = () => {
  const { colors } = useTheme()

  return (
    <div style={{
      backgroundColor: colors.bg.primary,
      borderRadius: '8px',
      padding: 'clamp(0.75rem, 2vw, 1rem)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: `2px solid ${colors.border.primary}`
    }}>
      <Skeleton width="60%" height="14px" style={{ marginBottom: '0.5rem' }} />
      <Skeleton width="80%" height="32px" style={{ marginBottom: '0.5rem' }} />
      <Skeleton width="50%" height="12px" />
    </div>
  )
}

// Skeleton for table rows
export const TableRowSkeleton = ({ columns = 9 }) => {
  const { colors } = useTheme()

  return (
    <tr style={{ borderTop: `1px solid ${colors.border.primary}` }}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} style={{ padding: '0.75rem' }}>
          <Skeleton height="16px" />
        </td>
      ))}
    </tr>
  )
}

// Skeleton for entire table
export const TableSkeleton = ({ rows = 5, columns = 9 }) => {
  const { colors } = useTheme()

  return (
    <div style={{
      backgroundColor: colors.bg.primary,
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      border: `1px solid ${colors.border.primary}`
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead style={{ backgroundColor: colors.bg.secondary }}>
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} style={{ padding: '0.75rem', textAlign: 'center' }}>
                  <Skeleton height="20px" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRowSkeleton key={i} columns={columns} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Add keyframe animation to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = `
    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `
  document.head.appendChild(style)
}

// Spinner component for buttons and inline loading
export const Spinner = ({ size = '20px', color = '#3b82f6' }) => {
  return (
    <div
      style={{
        width: size,
        height: size,
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
        display: 'inline-block'
      }}
    />
  )
}

// Loading button component
export const LoadingButton = ({
  children,
  loading = false,
  disabled = false,
  onClick,
  style = {},
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        opacity: loading || disabled ? 0.6 : 1,
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
        ...style
      }}
      {...props}
    >
      {loading && <Spinner size="16px" color="currentColor" />}
      {children}
    </button>
  )
}

// Full page loader
export const PageLoader = ({ message = 'Loading...' }) => {
  const { colors } = useTheme()

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '1rem'
    }}>
      <Spinner size="40px" />
      <p style={{ color: colors.text.secondary, fontSize: '0.875rem' }}>{message}</p>
    </div>
  )
}

// Add spin animation
if (typeof document !== 'undefined') {
  const existingStyle = document.querySelector('style[data-skeleton]')
  if (!existingStyle) {
    const style = document.createElement('style')
    style.setAttribute('data-skeleton', 'true')
    style.textContent = `
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `
    document.head.appendChild(style)
  }
}

export default Skeleton
